import React from 'react';
import { alpha, Step, StepButton, Stepper, Typography, useTheme } from '@mui/material';
import { ActivityStepConnector } from './components/ActivityStepConnector';
import { ActivityStepIcon } from './components/ActivityStepIcon';
import { ActivityStepLabel } from './components/ActivityStepLabel';

type Activity = {
  displayName: string;
  uid: string;
  isCompleted: boolean;
  description?: string;
};

export interface ActivityStepperProps {
  activities: Array<Activity>;
  onClick: (activity: Activity) => void;
  activeStep: number;
}

export const ActivityStepper = ({ activities, activeStep, onClick }: ActivityStepperProps): JSX.Element => {
  const theme = useTheme();
  return (
    <Stepper
      data-testid="activity-timeline"
      nonLinear
      orientation="vertical"
      activeStep={activeStep}
      connector={null} // We need to control the connector styles as it depends on the activity status, so we use our custom StepConnector below
    >
      {activities.map((activity, index) => {
        const isLast = index === activities.length - 1;

        return (
          <Step
            key={activity.uid}
            completed={activity.isCompleted}
            sx={{
              '& .MuiStepConnector-line': {
                borderColor: '#EAECF0',
              },
            }}
          >
            <StepButton
              onClick={() => onClick(activity)}
              role="link"
              aria-current={activeStep === index ? 'page' : undefined}
              sx={{
                padding: '4px',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: activeStep === index ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                borderRadius: activeStep === index ? '12px' : '0',
                '& .MuiStepLabel-iconContainer': {
                  padding: '4px',
                  marginRight: '4px',
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  borderRadius: '12px',
                  '& .MuiStepLabel-iconContainer': {
                    backgroundColor: '#D5D7DA',
                    padding: '4px',
                    borderRadius: '999px',
                  },
                  '& svg': {
                    strokeWidth: '0',
                  },
                  '& circle': {
                    strokeWidth: '0',
                  },
                },
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-visible': {
                  border: `2px solid ${theme.palette.primary.main}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  borderRadius: '12px',
                  '& .MuiTouchRipple-root': {
                    color: 'transparent',
                  },
                },
                '& .task-in-progress-icon': {
                  fill: 'white',
                },
              }}
            >
              <ActivityStepLabel
                componentsProps={{
                  label: {
                    'data-testid': `${activity.uid}-step-label`,
                  } as React.HTMLProps<HTMLSpanElement>,
                }}
                StepIconComponent={ActivityStepIcon}
              >
                {activity.displayName}
                {activity.description && (
                  <Typography variant="body2" sx={{ color: '#475467', lineHeight: '20px' }}>
                    {activity.description}
                  </Typography>
                )}
              </ActivityStepLabel>
            </StepButton>
            {!isLast && <ActivityStepConnector data-testid={`${activity.uid}-step-connector`} />}
          </Step>
        );
      })}
    </Stepper>
  );
};
